import Front from '@frontapp/plugin-sdk';

const ENDPOINT = import.meta.env.VITE_ODOO_ENDPOINT || '';
const ODOO_BASE_URL = (import.meta.env.VITE_ODOO_BASE_URL || '').replace(/\/$/, '');
const PARTNER_ID_FIELD_NAME = import.meta.env.VITE_PARTNER_ID_FIELD_NAME || 'Odoo Partner ID';
const AUTH_SECRET = new URLSearchParams(location.search).get('auth_secret') || '';

const app = document.getElementById('app');

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function extractContact(context) {
  const conversation = context?.conversation;
  const recipient = conversation?.recipient || {};
  const contact = recipient.contact || {};
  const handles = Array.isArray(contact.handles) ? contact.handles : [];

  // SDK types declare the handle kind as `type`, but the runtime payload has historically
  // used `source`; read either to stay compatible.
  const handleKind = (h) => h?.type ?? h?.source;
  const emailHandle = handles.find(h => handleKind(h) === 'email')?.handle;
  const phoneHandle = handles.find(h => handleKind(h) === 'phone')?.handle;

  let email = emailHandle || '';
  if (!email && recipient.handle && EMAIL_RE.test(recipient.handle)) {
    email = recipient.handle;
  }

  const phone = phoneHandle || '';
  const name = contact.name || recipient.name || email || recipient.handle || '';
  const front_contact_id = contact.id || '';

  const customAttrs = Array.isArray(contact.customAttributes) ? contact.customAttributes : [];
  const partnerAttr = customAttrs.find(a => a?.name === PARTNER_ID_FIELD_NAME);
  const rawPartnerId = partnerAttr?.value;
  const odoo_partner_id = rawPartnerId == null ? '' : String(rawPartnerId).trim();

  return { name, email, phone, front_contact_id, odoo_partner_id };
}

function renderGuidance(message) {
  app.innerHTML = `<div class="state">${escapeHtml(message)}</div>`;
}

function renderCard(info) {
  const { name, email, phone, front_contact_id, odoo_partner_id } = info;

  const fieldHtml = (label, value) => `
    <div class="field">
      <div class="label">${escapeHtml(label)}</div>
      <div class="value${value ? '' : ' empty'}">${value ? escapeHtml(value) : '—'}</div>
    </div>
  `;

  const isLinked = !!odoo_partner_id;
  const buttonLabel = isLinked ? 'Open partner in Odoo' : 'Link partner in Odoo';
  const disableAttr = (!isLinked && !(name || email || phone)) ? ' disabled' : '';

  app.innerHTML = `
    <div class="card">
      <h2>Contact</h2>
      ${fieldHtml('Name', name)}
      ${fieldHtml('Email', email)}
      ${fieldHtml('Phone', phone)}
      ${isLinked ? fieldHtml('Odoo Partner ID', odoo_partner_id) : ''}
      <button id="create-btn"${disableAttr}>${escapeHtml(buttonLabel)}</button>
      <div class="status info" id="status"></div>
    </div>
  `;

  const btn = document.getElementById('create-btn');
  const status = document.getElementById('status');

  if (isLinked) {
    btn.addEventListener('click', () => {
      if (!ODOO_BASE_URL) {
        setStatus('err', 'VITE_ODOO_BASE_URL is not configured in the build.');
        return;
      }
      const url = `${ODOO_BASE_URL}/odoo/contacts/${encodeURIComponent(odoo_partner_id)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    });
  } else {
    btn.addEventListener('click', async () => {
      if (!ENDPOINT) {
        setStatus('err', 'VITE_ODOO_ENDPOINT is not configured in the build.');
        return;
      }
      if (!AUTH_SECRET) {
        setStatus('err', 'Missing auth_secret from Front. Open this plugin via Front.');
        return;
      }

      btn.disabled = true;
      setStatus('info', 'Creating partner…');

      try {
        const res = await fetch(ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Front-Auth-Secret': AUTH_SECRET,
          },
          body: JSON.stringify({ name, email, phone, front_contact_id }),
        });

        let payload = null;
        try { payload = await res.json(); } catch { /* non-JSON body */ }

        if (!res.ok) {
          const msg = payload?.error || payload?.message || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        const id = payload?.id ?? payload?.result?.id;
        if (id == null) {
          throw new Error('Response did not include a partner id.');
        }

        const url = payload?.url || payload?.result?.url
          || (ODOO_BASE_URL ? `${ODOO_BASE_URL}/odoo/contacts/${id}` : '');
        if (url) window.open(url, '_blank', 'noopener,noreferrer');

        setStatus('ok', `Created partner #${id}.`);
      } catch (err) {
        setStatus('err', err?.message || 'Request failed.');
        btn.disabled = false;
      }
    });
  }

  function setStatus(kind, text) {
    status.className = `status ${kind}`;
    status.textContent = text;
  }
}

Front.contextUpdates.subscribe((context) => {
  switch (context.type) {
    case 'singleConversation': {
      const info = extractContact(context);
      renderCard(info);
      break;
    }
    case 'noConversation':
      renderGuidance('Open a conversation to create an Odoo partner from its contact.');
      break;
    case 'multiConversations':
      renderGuidance('Select a single conversation to create a partner.');
      break;
    default:
      renderGuidance('Unsupported Front context.');
  }
});
